import React, { useState } from 'react';
import { 
  History as HistoryIcon, Link2, FileText, Trash2, Search, 
  Filter, CheckCircle2, AlertTriangle, XCircle, Calendar, Clock, ChevronDown, Mail, GitCompare, FlaskConical
} from 'lucide-react';
import { Card, CardContent, GlassCard } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useApp } from '@/context/AppContext';
import { cn } from '@/lib/utils';

const getVerdictConfig = (item) => {
  const verdict = item.verdict || item.riskLevel || item.category;

  // Lab entries are informational, not safety verdicts
  if (item.type === 'phishing-lab' || verdict === 'Lab Simulation' || verdict === 'Lab Analysis') {
    return {
      icon: FlaskConical,
      color: 'text-primary',
      badge: 'glass',
      label: 'Lab Session',
    };
  }

  // Comparisons – treat as informational unless we can pin a side
  if (item.type === 'article-comparison') {
    return {
      icon: GitCompare,
      color: 'text-primary',
      badge: 'outline',
      label: verdict || 'Comparison',
    };
  }
  
  // Link verdicts: Safe, Caution, Dangerous
  // Article categories: High Credibility, Mixed / Uncertain, Low Credibility
  // Email verdicts: Likely Legitimate, Suspicious, High Phishing Risk
  
  if (verdict === 'Safe' || verdict === 'Low Risk' || verdict === 'Low' || verdict === 'Likely Legitimate' || verdict === 'High Credibility') {
    return {
      icon: CheckCircle2,
      color: 'text-success',
      badge: 'safe',
      label: item.type === 'link' ? 'Safe' : item.type === 'email' ? 'Legitimate' : item.type === 'article' ? 'High Credibility' : 'Low Risk',
    };
  } else if (verdict === 'Caution' || verdict === 'Medium Risk' || verdict === 'Medium' || verdict === 'Suspicious' || verdict === 'Mixed / Uncertain') {
    return {
      icon: AlertTriangle,
      color: 'text-warning',
      badge: 'suspicious',
      label: item.type === 'link' ? 'Caution' : item.type === 'email' ? 'Suspicious' : item.type === 'article' ? 'Mixed' : 'Medium Risk',
    };
  } else if (verdict === 'Dangerous' || verdict === 'High Risk' || verdict === 'High' || verdict === 'High Phishing Risk' || verdict === 'Low Credibility') {
    return {
      icon: XCircle,
      color: 'text-destructive',
      badge: 'danger',
      label: item.type === 'link' ? 'Dangerous' : item.type === 'email' ? 'Phishing' : item.type === 'article' ? 'Low Credibility' : 'High Risk',
    };
  }
  
  return {
    icon: AlertTriangle,
    color: 'text-muted-foreground',
    badge: 'unknown',
    label: 'Unknown',
  };
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'link':
      return Link2;
    case 'article':
      return FileText;
    case 'email':
      return Mail;
    case 'article-comparison':
      return GitCompare;
    case 'phishing-lab':
      return FlaskConical;
    default:
      return Link2;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'article-comparison': return 'Comparison';
    case 'phishing-lab': return 'Phishing Lab';
    default: return type;
  }
};

const HistoryItem = ({ item }) => {
  const config = getVerdictConfig(item);
  const Icon = config.icon;
  const TypeIcon = getTypeIcon(item.type);
  
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  return (
    <Card className="hover:shadow-md transition-all duration-200 animate-fade-in" data-testid={`history-item-${item.type}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Type Icon */}
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0",
            item.type === 'link' ? "bg-primary/10" : 
            item.type === 'email' ? "bg-accent/10" :
            item.type === 'article-comparison' ? "bg-primary/10" :
            item.type === 'phishing-lab' ? "bg-destructive/10" :
            "bg-secondary/10"
          )}>
            <TypeIcon className={cn(
              "w-5 h-5",
              item.type === 'link' ? "text-primary" : 
              item.type === 'email' ? "text-accent" :
              item.type === 'article-comparison' ? "text-primary" :
              item.type === 'phishing-lab' ? "text-destructive" :
              "text-secondary"
            )} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge variant={
                item.type === 'link' ? 'outline' : 
                item.type === 'email' ? 'glass' :
                item.type === 'article-comparison' ? 'outline' :
                item.type === 'phishing-lab' ? 'danger' :
                'secondary'
              } className="text-xs capitalize">
                {getTypeLabel(item.type)}
              </Badge>
              <Badge variant={config.badge}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
              {item.type === 'phishing-lab' && item.result?.mode && (
                <Badge variant="outline" className="text-[10px] capitalize">
                  {item.result.mode} mode
                </Badge>
              )}
            </div>
            
            <p className="text-sm font-medium text-foreground truncate mb-1">
              {item.input}
            </p>
            
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDate(item.timestamp)}
              </span>
              {item.safetyScore !== undefined && (
                <span className={cn(
                  item.safetyScore >= 80 ? 'text-success' : 
                  item.safetyScore >= 50 ? 'text-warning' : 'text-destructive'
                )}>
                  Safety: {item.safetyScore}/100
                </span>
              )}
              {item.riskScore !== undefined && item.type !== 'link' && (
                <span>Risk: {item.riskScore}/100</span>
              )}
              {item.credibilityScore !== undefined && (
                <span>Credibility: {item.credibilityScore}/100</span>
              )}
              {item.hardRedFlags !== undefined && item.hardRedFlags > 0 && (
                <span className="text-destructive font-medium">{item.hardRedFlags} critical</span>
              )}
              {item.flagCount !== undefined && item.flagCount > 0 && (
                <span className="text-warning">{item.flagCount} issues</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const History = () => {
  const { history, clearHistory } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, link, article, email
  const [filterVerdict, setFilterVerdict] = useState('all'); // all, safe, suspicious, dangerous

  // Filter history
  const filteredHistory = history.filter(item => {
    // Search filter
    if (searchQuery && !item.input.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Type filter
    if (filterType !== 'all' && item.type !== filterType) {
      return false;
    }
    
    // Verdict filter
    if (filterVerdict !== 'all') {
      const verdict = item.verdict || item.riskLevel || item.category;
      if (filterVerdict === 'safe' && verdict !== 'Safe' && verdict !== 'Low Risk' && verdict !== 'Low' && verdict !== 'Likely Legitimate' && verdict !== 'High Credibility') return false;
      if (filterVerdict === 'suspicious' && verdict !== 'Caution' && verdict !== 'Medium Risk' && verdict !== 'Medium' && verdict !== 'Suspicious' && verdict !== 'Mixed / Uncertain') return false;
      if (filterVerdict === 'dangerous' && verdict !== 'Dangerous' && verdict !== 'High Risk' && verdict !== 'High' && verdict !== 'High Phishing Risk' && verdict !== 'Low Credibility') return false;
    }
    
    return true;
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
              <HistoryIcon className="w-5 h-5 text-foreground" />
            </div>
            <h2 className="font-display text-2xl font-bold text-foreground">Verification History</h2>
          </div>
          <p className="text-muted-foreground">
            View and search your past verifications. {history.length} total records.
          </p>
        </div>
        
        {history.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clearHistory} className="text-destructive hover:text-destructive">
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
        )}
      </div>

      {/* Filters */}
      <GlassCard className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Input
              type="text"
              placeholder="Search history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
          
          {/* Type Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[140px] justify-between" data-testid="type-filter-trigger">
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  {filterType === 'all' ? 'All Types' : 
                   filterType === 'link' ? 'Links' : 
                   filterType === 'email' ? 'Emails' :
                   filterType === 'article' ? 'Articles' :
                   filterType === 'article-comparison' ? 'Comparisons' :
                   filterType === 'phishing-lab' ? 'Phishing Lab' : 'All Types'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterType('all')} data-testid="filter-type-all">All Types</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterType('link')} data-testid="filter-type-link">
                <Link2 className="w-4 h-4 text-primary mr-2" /> Links Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterType('article')} data-testid="filter-type-article">
                <FileText className="w-4 h-4 text-secondary mr-2" /> Articles Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterType('email')} data-testid="filter-type-email">
                <Mail className="w-4 h-4 text-accent mr-2" /> Emails Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterType('article-comparison')} data-testid="filter-type-comparison">
                <GitCompare className="w-4 h-4 text-primary mr-2" /> Comparisons
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterType('phishing-lab')} data-testid="filter-type-phishing-lab">
                <FlaskConical className="w-4 h-4 text-destructive mr-2" /> Phishing Lab
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Verdict Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="min-w-[140px] justify-between">
                <span className="flex items-center gap-2">
                  {filterVerdict === 'all' && <Filter className="w-4 h-4" />}
                  {filterVerdict === 'safe' && <CheckCircle2 className="w-4 h-4 text-success" />}
                  {filterVerdict === 'suspicious' && <AlertTriangle className="w-4 h-4 text-warning" />}
                  {filterVerdict === 'dangerous' && <XCircle className="w-4 h-4 text-destructive" />}
                  {filterVerdict === 'all' ? 'All Results' : 
                   filterVerdict.charAt(0).toUpperCase() + filterVerdict.slice(1)}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterVerdict('all')}>All Results</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterVerdict('safe')}>
                <CheckCircle2 className="w-4 h-4 text-success mr-2" /> Safe
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterVerdict('suspicious')}>
                <AlertTriangle className="w-4 h-4 text-warning mr-2" /> Suspicious
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterVerdict('dangerous')}>
                <XCircle className="w-4 h-4 text-destructive mr-2" /> Dangerous
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </GlassCard>

      {/* History List */}
      {filteredHistory.length > 0 ? (
        <div className="space-y-3">
          {filteredHistory.map((item) => (
            <HistoryItem key={item.id} item={item} />
          ))}
        </div>
      ) : history.length > 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-display font-semibold text-lg text-foreground mb-2">No matches found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or filters.
          </p>
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <HistoryIcon className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-display font-semibold text-lg text-foreground mb-2">No history yet</h3>
          <p className="text-muted-foreground mb-6">
            Your verified links and articles will appear here.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Start Verifying
          </Button>
        </div>
      )}
    </div>
  );
};

export default History;
